import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Alert } from "react-bootstrap";
import Image from 'react-bootstrap/Image';

import { useNavigate } from 'react-router-dom'; // Usa useNavigate para la navegación en react-router-dom v6
import { fetchGetData, fetchPostData } from '../dataVar/apiService';
import logger from '../Utils/logger';
import { APP_VERSION_LABEL } from '../appVersion';
import 'bootstrap/dist/css/bootstrap.min.css'; // Asegúrate de importar Bootstrap CSS
import '../Utils/styles.css'; // Importa los estilos globales si es necesario
import appVar from '../dataVar/appVar';

import 'primereact/resources/themes/saga-blue/theme.css';  // Tema de PrimeReact
import 'primereact/resources/primereact.min.css';  // Estilos de PrimeReact

import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";

// Evita que el navegador sirva una versión vieja cacheada de las imágenes públicas
// (logos, fondos) al recargar después de reemplazar el archivo en el servidor.
const CACHE_BUST = Date.now();

// reCAPTCHA v3: la acción debe coincidir con RECAPTCHA_ACCION de ValidaLoginController.vb
const RECAPTCHA_ACCION = "login";
const RECAPTCHA_SCRIPT_ID = "recaptcha-v3-script";



const Login = () => {
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [password, setPassword] = useState("");
    const [username, setUsername] = useState("");
    const [selectedRegional, setSelectedRegional] = useState(null);
    const [regional, setRegional] = useState([]);
    const [regionalEnabled, setRegionalEnabled] = useState(false);
    const navigate = useNavigate(); // useNavigate para redirigir

    // Estados para manejar errores individuales
    const [usernameError, setUsernameError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [regionalError, setRegionalError] = useState("");
    const [captchaError, setCaptchaRError] = useState("");

    const [enviando, setEnviando] = useState(false);

    // reCAPTCHA v3 (invisible, por puntuación). La llave pública se carga en
    // index.js desde config.json; la verificación del token la hace
    // ValidaLoginController en la API. Sin siteKeyV3 no se pide token.
    const siteKeyV3 = appVar.siteKeyV3;

    useEffect(() => {
        if (!siteKeyV3 || document.getElementById(RECAPTCHA_SCRIPT_ID)) return;
        const script = document.createElement("script");
        script.id = RECAPTCHA_SCRIPT_ID;
        script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKeyV3)}`;
        script.async = true;
        document.head.appendChild(script);
    }, [siteKeyV3]);

    const obtenerTokenCaptcha = () => new Promise((resolve, reject) => {
        const g = window.grecaptcha;
        if (!g || typeof g.ready !== "function") {
            reject(new Error("El script de reCAPTCHA no cargó"));
            return;
        }
        g.ready(() => {
            g.execute(siteKeyV3, { action: RECAPTCHA_ACCION }).then(resolve, reject);
        });
    });

    // Limpiar variables globales al cargar el formulario
    useEffect(() => {
        appVar.gCodUsuario = null;
        appVar.gNomUsuario = null;
        appVar.gOpcionReg = null;
        appVar.gNIT = null;
        appVar.gCodRegional = null;
        appVar.gNomRegional = null;
        appVar.gCodOficina = null;
        appVar.gDigitador = null;
        appVar.gBackground = null;
    }, []);

    const usernameRegex = /^[1-9][0-9]{5,13}$/;
    const passwordRegex = /^(?=.*[a-zñáéíóúü])(?=.*[A-ZÑÁÉÍÓÚÜ])(?=.*\d)(?=.*[$@$!?&+*])[A-Za-zñÑáéíóúüÁÉÍÓÚÜ\d$@$!?&+*]{8,15}$/;

    // Llegar al login (incluido "Salir" del menú) cierra la sesión del API.
    useEffect(() => {
        appVar.gTokenApi = "";
    }, []);

    useEffect(() => {
        const fetchDataRegionales = async () => {
            try {
                const response = await fetchGetData('api/TablasProceso/regionales', '0/0');
                const transformedData = response.map(regional => ({
                    label: regional.Nom_Regional,
                    value: regional.Cod_Regional
                }));
                setRegional(transformedData);
            } catch (error) {
                logger.error('Error fetching regional:', error);
            }
        };
        fetchDataRegionales();
    }, []);

    const handleValidaUsuario = async (username) => {
        if (usernameRegex.test(username)) {
            try {
                const validaUsuArray = await fetchGetData('api/Login-Acceso/ValidaUsuario', username);
                if (Array.isArray(validaUsuArray) && validaUsuArray.length > 0) {
                    const validaUsu = validaUsuArray[0];
                    if (validaUsu.hasOwnProperty('Error')) {
                        const error = validaUsu.Error;

                        if (error === "OK") {
                            setUsernameError(""); // Limpiar error

                            if (validaUsu.Mensaje) {
                                alert(validaUsu.Mensaje);
                            } else {
                                // Habilitar o deshabilitar el select de regional según la respuesta
                                if (validaUsu.OpcionReg === 1) {
                                    setRegionalEnabled(true);
                                } else {
                                    setRegionalEnabled(false);
                                    // Establecer la regional por defecto si no se permite seleccionar
                                    const foundRegional = regional.find(reg => reg.value === validaUsu.Cod_Regional);
                                    if (foundRegional) {
                                        setSelectedRegional(foundRegional.value);
                                    }
                                }
                            }
                        } else {

                            setUsernameError(error);
                        }
                    } else {
                        setError("Respuesta inesperada del servidor.");
                    }
                } else {
                    setError("La respuesta del servidor no es válida.");
                }
            } catch (error) {
                logger.error("Error obteniendo respuesta:", error);
                setError("Error al procesar la solicitud");
            }
        } else {
            setUsernameError("Usuario no válido.");
        }
    };

    const handleValidaLogin = async (e) => {
        e.preventDefault(); // Prevenir el comportamiento predeterminado del formulario

        // Reiniciar errores
        setUsernameError("");
        setPasswordError("");
        setRegionalError("");
        setCaptchaRError("");

        let hasError = false;

        // Validar usuario
        if (!username) {
            setUsernameError("Ingrese su usuario.");
            hasError = true;
        } else if (!usernameRegex.test(username)) {
            setUsernameError("Usuario no válido.");
            hasError = true;
        }

        // Validar contraseña
        if (!password) {
            setPasswordError("Ingrese su contraseña.");
            hasError = true;
        } else if (!passwordRegex.test(password)) {
            setPasswordError("Contraseña no válida. Entre 8 y 15 caracteres, incluir una letra mayúscula, una minúscula, un número y un carácter especial $@!?&+");
            hasError = true;
        }

        // Validar Unidad R
        if (!selectedRegional) {
            setRegionalError("Seleccione una Regional.");
            hasError = true;
        }

        // Si hay errores, detener la ejecución
        if (hasError) {
            return;
        }


        // Si no hay errores, continuar con la lógica de inicio de sesión
        setEnviando(true);
        try {

            // reCAPTCHA v3: token invisible, la puntuación la verifica el backend
            let captchaToken = "";
            if (siteKeyV3) {
                try {
                    captchaToken = await obtenerTokenCaptcha();
                } catch (err) {
                    logger.warn("reCAPTCHA v3 no disponible", err);
                    setCaptchaRError("No se pudo cargar el CAPTCHA. Revise su conexión y recargue la página.");
                    return;
                }
            }

            const regionalValue = selectedRegional?.value || selectedRegional;

            const data = {
                Cod_Usuario: username || "",
                Password: password || "",
                Regional: regionalValue || "",
                CaptchaToken: captchaToken
            };

            const validaLogArray = await fetchPostData('api/Login-Acceso/ValidaLogin', data);

            if (Array.isArray(validaLogArray) && validaLogArray.length > 0) {
                const validaLog = validaLogArray[0];

                if (validaLog.hasOwnProperty('Error')) {
                    const error = validaLog.Error;
                    const mensaje = validaLog.Mensaje || '';

                    if (error === "OK") {

                        // Si hay mensaje informativo, mostrarlo pero permitir el login
                        if (!validaLog.Token) {
                            setError("El servidor no entregó el token de sesión. Contacte al administrador (API desactualizado).");
                            return;
                        }

                        // Configurar variables globales (se hace siempre)
                        appVar.gTokenApi = validaLog.Token;
                        appVar.gCodUsuario = username;
                        appVar.gNomUsuario = validaLog.Nom_Usuario;
                        appVar.gOpcionReg = validaLog.OpcionReg;
                        appVar.gCodOficina = validaLog.CodOficina;
                        appVar.gNIT = validaLog.Nit;
                        appVar.gDigitador = validaLog.Digitador;
                        appVar.gFecProceso = validaLog.FecProceso;

                        // Buscar la regional seleccionada en el array
                        const selectedRegionalData = regional.find(reg => reg.value === selectedRegional);
                        if (selectedRegionalData) {
                            appVar.gCodRegional = selectedRegionalData.value;
                            appVar.gNomRegional = selectedRegionalData.label;
                        }

                        // Validar mensaje y redirigir
                        if (mensaje && mensaje.trim() !== '') {
                            setError(mensaje);

                            // Si el mensaje es "Debe Cambiar Contraseña", redirigir a CambiaPassword_Inic
                            if (mensaje.includes("Debe Cambiar Contraseña") || mensaje.includes("Debe cambiar contraseña")) {
                                setTimeout(() => {
                                    navigate("/CambiaPassword_Inic");
                                }, 2000);
                            } else {
                                // Para otros mensajes, dar tiempo para leer y luego ir a Inicio
                                setTimeout(() => {
                                    navigate("/Inicio");
                                }, 5000);
                            }
                        } else {
                            // Login exitoso sin mensaje
                            navigate("/Inicio");
                        }

                    } else if (error.includes("Inactivo por cambio de Contraseña") ||
                        error.includes("Inactivo por cambio") ||
                        error === "Inactivo por cambio de Contraseña") {
                        setError("");
                        setTimeout(() => {
                            navigate("/CambiaPassword_Inic");
                        }, 2000);

                    } else if (error === "Contraseña incorrecta" || error.includes("Contraseña")) {
                        setPasswordError(error);

                    } else {
                        // Cualquier otro error
                        setError(error);
                    }
                } else {
                    setError("Respuesta inesperada del servidor.");
                }
            } else {
                setError("La respuesta del servidor no es válida.");
            }
        } catch (error) {
            setError(`Error al procesar la solicitud: ${error.message || error}`);
        } finally {
            setEnviando(false);
        }
    };

    const handleForgotPassword = () => {
        navigate('/OlvidoPassword')
    };

    const LogoXUECia = '/imagenes/LogoXUECia.png';
    const fondo = `/imagenes/Fondo_L.png?v=${CACHE_BUST}`;
    const Version = APP_VERSION_LABEL
    const LogoCliente = `/imagenes/LogoCliente.png?v=${CACHE_BUST}`;

    return (
        <Container fluid>
            <Row>
                <Col sm={6}>
                    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Container className="d-flex align-items-center justify-content-center flex-grow-1">
                            <div className="col-lg-7" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', padding: '20px', borderRadius: '20px' }}>

                                <Row>

                                    <img
                                        src={LogoCliente}
                                        alt="Logo"
                                        style={{
                                            maxHeight: 150,
                                            maxWidth: '100%',
                                            width: 'auto',
                                            height: 'auto',
                                            objectFit: 'contain',
                                            marginTop: 0,
                                            marginBottom: 5
                                        }}
                                    />

                                </Row>

                                <Row>
                                    <h3>Login</h3>
                                </Row>

                                <Form onSubmit={handleValidaLogin}> {/* Agregar onSubmit al formulario */}
                                    {/* Campo de usuario */}
                                    <div className="p-inputgroup">
                                        <span className="p-inputgroup-addon">
                                            <i className="pi pi-user"></i>
                                        </span>
                                        <InputText
                                            value={username}
                                            type="number"
                                            onChange={(e) => setUsername(e.target.value)}
                                            onBlur={() => handleValidaUsuario(username)}
                                            placeholder="Ingrese su usuario"
                                            className={usernameError ? 'p-invalid' : ''}
                                            autoComplete="off"
                                        />
                                    </div>
                                    {usernameError && <small className="p-error">{usernameError}</small>}

                                    {/* Campo de contraseña */}
                                    <div className="p-inputgroup mt-3">
                                        <span className="p-inputgroup-addon">
                                            <i className="pi pi-key"></i>
                                        </span>
                                        <InputText
                                            type="password"
                                            placeholder="Ingrese su contraseña"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className={passwordError ? 'p-invalid' : ''}
                                            autoComplete="new-password"
                                        />
                                    </div>
                                    {passwordError && <small className="p-error">{passwordError}</small>}

                                    <div className="p-inputgroup mt-3">
                                        <span className="p-inputgroup-addon">
                                            <i className="pi pi-slack"></i>
                                        </span>
                                        <Dropdown
                                            value={selectedRegional}
                                            options={regional}
                                            onChange={(e) => {
                                                setSelectedRegional(e.value); // Usar e.value en lugar de e.target.value
                                                appVar.gCodRegional = e.value;
                                            }}
                                            optionLabel="label"
                                            placeholder="Seleccionar Regional"
                                            className={`dropdown-small-text ${regionalError ? 'p-invalid' : ''}`}
                                            style={{
                                                width: '100%'
                                            }}
                                            panelStyle={{
                                                fontSize: '12px'
                                            }}
                                            disabled={!regionalEnabled}
                                        />
                                    </div>
                                    {regionalError && <small className="p-error">{regionalError}</small>}

                                    {/* Botón de inicio de sesión */}
                                    <div className="d-flex justify-content-center mt-3">
                                        <Button label="Iniciar Sesión" icon="pi pi-sign-in" type="submit" className="p-button-primary w-full" loading={enviando} disabled={enviando} />
                                    </div>
                                    {captchaError && <small className="p-error">{captchaError}</small>}

                                    {/* reCAPTCHA v3: el sello flotante se oculta (styles.css), así que
                                        Google exige mostrar este aviso en su lugar */}
                                    {siteKeyV3 && (
                                        <div className="text-center mt-1" style={{ fontSize: "10px", color: "#9a9a9a" }}>
                                            Protegido por reCAPTCHA de Google ·{" "}
                                            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacidad</a> ·{" "}
                                            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Términos</a>
                                        </div>
                                    )}

                                    {/* Enlace para olvidar contraseña */}
                                    <div className="d-flex justify-content-center mt-1">
                                        <Button label="¿Olvidó su contraseña?" className="p-button-text mt-2" onClick={handleForgotPassword} />
                                    </div>

                                    {/* Imagen en la parte inferior */}
                                    <div className="text-center mb-3">
                                        <Image src={LogoXUECia} width={100} />
                                    </div>

                                    <div className="text-center mt-2">
                                        <small style={{ color: "#b0b0b0" }}>{Version}</small>
                                    </div>

                                    <div className="text-center mt-2">
                                        {(error || success) && (
                                            <Row className="mt-3">
                                                <Col md={12}>
                                                    {error && <Alert variant="danger">{error}</Alert>}
                                                    {success && <Alert variant="success">{success}</Alert>}
                                                </Col>
                                            </Row>
                                        )}
                                    </div>
                                </Form>
                            </div>
                        </Container>

                    </div>
                </Col>

                <Col sm={6} className='d-none d-sm-block px-0'>
                    <Image
                        src={fondo}
                        alt="Login image"
                        fluid
                        className="w-100"
                        style={{
                            objectFit: 'fill',
                            width: '100%',
                            minHeight: '100vh',
                            height: '100vh'
                        }}
                    />
                </Col>

            </Row>

        </Container>


    );
};

export default Login;